import { notificationEmitter } from '../providers/NotificationContext';
import { NotificationType } from '../components/shared/NotificationAlert';

export type BackendValidationErrorData = {
  code?: string;
  message?: string;
  details?: Record<string, any>;
};

export function formatBackendValidationWarning(
  errorData: BackendValidationErrorData | any
): { title: string; description?: string } | null {
  const code = String(errorData?.code || '').trim();
  const details: Record<string, any> = (errorData?.details || {}) as any;

  if (code !== 'VALIDATION_FAILED') return null;

  // Known validation translations
  const messages: string[] = [];
  let title = 'Validation failed';

  if (typeof details?.appointTimeValid === 'string' && details.appointTimeValid.trim()) {
    // 预约时间必须距离当前时间至少3小时，且是30分钟的整数倍
    title = 'Schedule Failed';
    messages.push('Pick a time at least 3 hours from now, on a 30‑minute interval.');
  }
  if (typeof details?.appointTime === 'string' && details.appointTime.trim()) {
    // 预约时间点必须是未来时间
    // (Covered by the rule above, but keep a fallback if it appears alone.)
    title = 'Schedule Failed';
    if (!messages.length) messages.push('Pick a time in the future.');
  }
  if (typeof details?.dateRangeValid === 'string' && details.dateRangeValid.trim()) {
    // 日期范围不能超过30天，且结束日期不能早于开始日期
    title = 'Invalid Date Range';
    messages.push('Date range must be within 30 days, and end date cannot be earlier than start date.');
  }

  if (!messages.length) return null;

  return {
    title,
    description: messages.join('\n'),
  };
}

interface NotificationConfig {
  message: string;
  description?: string;
  duration?: number;
}

/**
 * NotificationService - Singleton service for triggering global notifications
 * 
 * This service can be called from anywhere (including non-React code like axios interceptors)
 * and will emit events that the NotificationProvider picks up to display MUI Snackbars.
 */
class NotificationService {
  private static instance: NotificationService;

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private constructor() {
    // No initialization needed - the Provider handles rendering
  }

  show(type: NotificationType, config: NotificationConfig) {
    notificationEmitter.emit({
      type,
      message: config.message,
      description: config.description,
      duration: config.duration ? config.duration * 1000 : 4500, // Convert seconds to ms
    });
  }

  success(message: string, description?: string) {
    this.show('success', { message, description });
  }

  error(message: string, description?: string) {
    this.show('error', { message, description });
  }

  warning(message: string, description?: string) {
    this.show('warning', { message, description });
  }

  info(message: string, description?: string) {
    this.show('info', { message, description });
  }

  sessionExpired() {
    this.show('warning', {
      message: 'Authentication Required',
      description: 'Please log in to continue.',
      duration: 6,
    });
  }

  networkError() {
    this.show('error', {
      message: 'Network Error',
      description: 'Unable to connect to the server. Please check your internet connection and try again.',
      duration: 5,
    });
  }

  destroy() {
    // No-op for now - notifications auto-dismiss
    // Could be extended to emit a 'clear all' event if needed
  }
}

export default NotificationService.getInstance();
